// Kismet: assets/table-sticky-header.js.liquid

const observer2 = function observeElementAtTop(el, offset = "0px 0px 0px 0px", callback, threshold = 0) {
  let margin = offset.includes(" ") ? offset : `-${offset}px 0px 0px 0px`;
  const intersectionObserver = new IntersectionObserver(
    ([entry]) => {
      callback(entry);
    },
    {
      root: null,
      rootMargin: margin,
      threshold: threshold,
    }
  );

  intersectionObserver.observe(el);
  return intersectionObserver;
};

class TableStickyHeader extends HTMLElement {
  constructor() {
    super();
    this._gen2TableHeader = null;
    this._gen2TableBody = null;
    this._tableBodyObserver = null;
    this._shouldStickyHeader = false; // 跟踪gen2 table-header是否应该吸附
    this._scrollSyncHandler = null;
    this._isScrollSyncing = false; // 防止循环滚动
    this._tableWrapper = null;
    this._isHeaderStuck = false; // 跟踪header是否已经吸附
    this._secondaryNavSection = null;
    this._headerContainer = null;
  }

  connectedCallback() {
    this._init();
    this._setupScrollListener();
  }

  _init() {
    this._findGen2Table();
    this._secondaryNavSection = document.querySelector(".shopify-section.section-secondary-navigation");
    this._headerContainer = document.querySelector(".header-container");
    this._setupGen2TableObserver();
  }

  _setupScrollListener() {
    this._scrollHandler = this._throttle(() => {
      this._updateGen2TableHeaderPosition();
    }, 10); // Use a smaller throttle for smoother position updates
    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    this._resizeHandler = this._throttle(() => {
      if (this._tableBodyObserver) {
        this._tableBodyObserver.disconnect();
        this._tableBodyObserver = null;
      }
      this._clearScrollSync();
      this._init(); // Re-initialize to get fresh elements and observers
      this._updateGen2TableHeaderPosition();
      if (this._isHeaderStuck) {
        const tableWrapperRect = this._tableWrapper.getBoundingClientRect();
        this._gen2TableHeader.style.width = tableWrapperRect.width + "px";
        this._gen2TableHeader.style.left = tableWrapperRect.left + "px";
      }
    }, 50);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  _findGen2Table() {
    // 查找gen2表格（只有一个）
    const gen2Table = this; // The custom element is the section itself
    if (gen2Table) {
      this._gen2TableHeader = gen2Table.querySelector(".table-header");
      this._gen2TableBody = gen2Table.querySelector(".table-body");
      this._tableWrapper = gen2Table.querySelector(".table-wrapper");
    } else {
      this._gen2TableHeader = null;
      this._gen2TableBody = null;
      this._tableWrapper = null;
    }
  }

  _setupGen2TableObserver() {
    // 清理现有的observer
    if (this._tableBodyObserver) {
      this._tableBodyObserver.disconnect();
      this._tableBodyObserver = null;
    }

    // 重置状态
    this._shouldStickyHeader = false;

    // 只有当gen2表格的body存在时才设置inView检测
    if (this._gen2TableBody && this._gen2TableHeader) {
      const secondaryNavHeight = this._secondaryNavSection
        ? this._secondaryNavSection.getBoundingClientRect().height
        : 0;
      const headerHeight = this._headerContainer ? this._headerContainer.getBoundingClientRect().height : 0;
      const tableheaderHeight = this._gen2TableHeader.getBoundingClientRect().height;

      const totalOffset = secondaryNavHeight + headerHeight; // When to start sticking
      const bottomOffset = Math.max(0, window.innerHeight - (totalOffset + tableheaderHeight) - 180); // Margin from bottom to stop sticking

      // 使用inView函数检测table-body的可视状态
      this._tableBodyObserver = observer2(
        this._gen2TableBody,
        `-${totalOffset}px 0px -${bottomOffset}px 0px`,
        (entry) => {
          this._shouldStickyHeader = entry.isIntersecting;
          this._updateGen2TableHeaderPosition();
        }
      );
    }
  }

  _updateGen2TableHeaderPosition() {
    if (!this._gen2TableHeader) return;

    let stickyTopOffset = 0;
    if (this._secondaryNavSection) {
      const navRect = this._secondaryNavSection.getBoundingClientRect();
      stickyTopOffset = navRect.bottom;
    } else if (this._headerContainer) {
      const headerRect = this._headerContainer.getBoundingClientRect();
      stickyTopOffset = headerRect.bottom;
    }

    if (stickyTopOffset < 0) {
      stickyTopOffset = 0;
    }

    // 根据table-body状态决定是否吸附
    if (this._shouldStickyHeader) {
      if (!this._isHeaderStuck) {
        const tableWrapperRect = this._tableWrapper.getBoundingClientRect();
        this._gen2TableHeader.style.position = "fixed";
        this._gen2TableHeader.style.zIndex = "100";
        this._gen2TableHeader.style.width = tableWrapperRect.width + "px";
        this._gen2TableHeader.style.left = tableWrapperRect.left + "px"; // Align horizontally
        this._gen2TableHeader.style.transition = "top 0.3s ease"; // Smooth transition for top
        this._gen2TableHeader.style.overflowX = "auto"; // Make header scrollable
        this._gen2TableHeader.style.backgroundColor = "#ffffff"; // Avoid transparency issues
        this._isHeaderStuck = true;
        this._setupScrollSync();
      }
      this._gen2TableHeader.style.top = `${stickyTopOffset}px`;
    } else {
      if (this._isHeaderStuck) {
        // 不需要吸附时恢复原始状态
        this._gen2TableHeader.style.position = "sticky";
        this._gen2TableHeader.style.top = "0";
        this._gen2TableHeader.style.zIndex = "1";
        this._gen2TableHeader.style.width = "";
        this._gen2TableHeader.style.left = "";
        this._gen2TableHeader.style.transition = "";
        this._gen2TableHeader.style.overflowX = "";
        this._gen2TableHeader.style.backgroundColor = "";
        this._isHeaderStuck = false;
        // 清理滚动同步
        this._clearScrollSync();
      }
    }
  }

  _setupScrollSync() {
    // 清理之前的滚动监听器
    this._clearScrollSync();

    if (!this._tableWrapper || !this._gen2TableHeader) {
      return;
    }

    // 创建水平滚动同步处理器
    this._scrollSyncHandler = (event) => {
      if (this._isScrollSyncing) return;

      this._isScrollSyncing = true;
      try {
        const source = event.target;
        const target = source === this._tableWrapper ? this._gen2TableHeader : this._tableWrapper;
        if (target.scrollLeft !== source.scrollLeft) {
          target.scrollLeft = source.scrollLeft;
        }
      } catch (error) {
        console.error("Scroll sync error:", error);
      } finally {
        requestAnimationFrame(() => {
          this._isScrollSyncing = false;
        });
      }
    };

    // 添加水平滚动监听器
    this._tableWrapper.addEventListener("scroll", this._scrollSyncHandler, { passive: true });
    this._gen2TableHeader.addEventListener("scroll", this._scrollSyncHandler, { passive: true });
  }

  _clearScrollSync() {
    if (this._scrollSyncHandler && this._tableWrapper && this._gen2TableHeader) {
      this._tableWrapper.removeEventListener("scroll", this._scrollSyncHandler);
      this._gen2TableHeader.removeEventListener("scroll", this._scrollSyncHandler);
      this._scrollSyncHandler = null;
    }
  }

  _throttle(func, limit) {
    let inThrottle;
    return (...args) => {
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
}

if (!window.customElements.get("table-sticky-header")) {
  window.customElements.define("table-sticky-header", TableStickyHeader);
}
